import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { Button, Typography } from "@mui/material";

import { IN_PROGRESS, isSuccess } from "commons/src/helpers/RequestHelpers";
import { useComponentState } from "commons/src/helpers/CustomHooks";
import { setUserPreferences } from "commons/src/reducers/clientPortalUserActions";

import { getCorrespondenceDeliveryOptions } from "../../../components/DataOperations";
import { Message } from "../../../components/Message";
import {
    ERROR as ERROR_SEVERITY,
    INFO as INFO_SEVERITY,
    getRefetchMessageAndAction
} from "../../../helpers/ErrorHelpers";
import { retryFetch } from "../../../components/DataOperations";
import { requestObject } from "../../../reducers/requestsActions";
import { Accordion } from "../../../components/Accordion";
import { DocumentPreferencesEdit } from "./DocumentPreferencesEdit";
import { deliveryOptionProp } from "../../../constants/propTypes";
import { GET_CLIENT_PREFERENCES } from "../../../components/DataOpsConstants";

export function DocumentPreferences({
    allDeliveryOptions,
    currentPreferences = [],
    clientPreferencesRequestStatus = { status: IN_PROGRESS },
    isAdviser
}) {
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentEdit, setCurrentEdit] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [currentState, changeStateTo, { LOADING, ERROR, SUCCESS, DEFAULT }] = useComponentState();

    const getOptions = useCallback(async () => {
        try {
            changeStateTo(LOADING);
            await getCorrespondenceDeliveryOptions();
            changeStateTo(SUCCESS);
        } catch {
            changeStateTo(ERROR);
        }
    }, [ERROR, LOADING, SUCCESS, changeStateTo]);

    useEffect(() => {
        if (!allDeliveryOptions?.length && currentState === DEFAULT) {
            getOptions();
        } else if (allDeliveryOptions?.length) {
            changeStateTo(SUCCESS);
        }
    }, [DEFAULT, SUCCESS, currentState, allDeliveryOptions, getOptions, changeStateTo]);

    const refetch = () =>
        retryFetch(clientPreferencesRequestStatus.href, setUserPreferences, GET_CLIENT_PREFERENCES);

    const { message: requestMessage, action } = getRefetchMessageAndAction(
        clientPreferencesRequestStatus,
        refetch
    );

    const closeModal = () => setIsModalOpen(false);

    const requestsFinished = isSuccess(clientPreferencesRequestStatus.status);

    const getSelectedPreferenceForEdit = (currentPrefs, selectedPrefCode) =>
        currentPrefs.find(pref => pref.documentType.codeCode === selectedPrefCode);

    const handleAccordionToggle = () => {
        if (isAccordionOpen) {
            setSuccessMessage("");
        }
        setIsAccordionOpen(!isAccordionOpen);
    };

    return (
        <Container>
            <Accordion
                title="Document preferences"
                isOpen={isAccordionOpen}
                handleChange={handleAccordionToggle}
            >
                <Card>
                    <Message
                        severity={ERROR_SEVERITY}
                        text={requestMessage}
                        errorCode={clientPreferencesRequestStatus.errorCode}
                        shown={!!requestMessage}
                        padding="0px"
                        action={action}
                    />
                    <Message
                        severity={INFO_SEVERITY}
                        text={successMessage}
                        shown={!!successMessage}
                        padding="0px"
                    />
                    {requestsFinished && (
                        <Container>
                            <Typography variant="body2">
                                Please choose how you would like to receive the following
                                correspondence:
                            </Typography>
                            <ListOfOptions>
                                {currentPreferences.map(preference => (
                                    <ListItem
                                        key={preference.documentType.codeDescription}
                                        data-testid="document-delivery-preference"
                                    >
                                        <Typography variant="body2">{`${preference.documentType.codeDescription}: ${preference.deliveryOption.codeDescription}`}</Typography>
                                        <Button
                                            onClick={() => {
                                                setCurrentEdit(preference);
                                                setIsModalOpen(true);
                                            }}
                                            color="secondary"
                                        >
                                            Edit
                                        </Button>
                                    </ListItem>
                                ))}
                            </ListOfOptions>
                        </Container>
                    )}
                </Card>
            </Accordion>
            <DocumentPreferencesEdit
                isOpen={isModalOpen}
                allDeliveryOptions={allDeliveryOptions}
                handleClose={closeModal}
                deliveryOptionsFetchState={currentState}
                getOptions={getOptions}
                isAdviser={isAdviser}
                currentPreference={getSelectedPreferenceForEdit(
                    currentPreferences,
                    currentEdit?.documentType?.codeCode
                )}
                handlePrefrencesUpdate={message => {
                    closeModal();
                    setSuccessMessage(message);
                }}
            />
        </Container>
    );
}

const Container = styled.div`
    margin-top: 1rem;
`;

const ListOfOptions = styled.ul`
    margin-top: 0.75em;
    list-style: none;
`;

const ListItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    margin-bottom: 0.75em;
`;

const Card = styled.div`
    width: 100%;
    background: ${props => props.theme.tertiary};
    overflow: auto;
    padding: 1.5em;
`;
DocumentPreferences.propTypes = {
    currentPreferences: PropTypes.arrayOf(
        PropTypes.shape({
            documentType: deliveryOptionProp,
            deliveryOption: deliveryOptionProp
        })
    ),
    clientPreferencesRequestStatus: requestObject,
    allDeliveryOptions: PropTypes.arrayOf(deliveryOptionProp),
    isAdviser: PropTypes.bool
};
